import type * as Y from 'yjs';

import { type DisposableElement, isDisposableElement } from './types.ts';

export class LazyMapElement<T> implements DisposableElement {
  #value: T;

  constructor(
    private input: Y.Map<unknown>,
    private key: string,
    private mapper: (input: unknown) => T | undefined,
    private initialize: () => any,
    private createDefault: (input: Y.Map<unknown>, attach: (val: Y.Map<any>) => void) => T
  ) {
    input.observe(this.onValueChanged);
    this.#value = this.loadValue();
  }

  get value(): T {
    return this.#value;
  }

  set(value: Y.AbstractType<any>): void {
    this.input.set(this.key, value);
    this.#value = this.loadValue();
  }

  private loadValue(): T {
    const valueFromStorage = this.mapper(this.input.get(this.key));
    if (valueFromStorage) {
      return valueFromStorage;
    } else {
      const inputValue = this.initialize();
      return this.createDefault(inputValue, (val: Y.Map<any>) => {
        this.input.set(this.key, val);

        if (!val.doc) {
          throw new Error('yjs element should be connected to document');
        }
      });
    }
  }

  private onValueChanged = (e: Y.YMapEvent<any>) => {
    if (e.keysChanged.has(this.key)) {
      if (this.#value) {
        if (isDisposableElement(this.#value)) {
          this.#value.dispose();
        }
      }

      this.#value = this.loadValue();
    }
  };

  dispose(): void {
    this.input.unobserve(this.onValueChanged);

    if (this.#value && isDisposableElement(this.#value)) {
      this.#value.dispose();
    }
  }
}
